import { Controller } from "@hotwired/stimulus"

// EventType and EventCategory constants
const EventCategory = {
  Feeding: 'Feeding',
  Care: 'Care',
  Health: 'Health',
  Growth: 'Growth'
}

const EventType = {
  Formula: 'Formula',
  Sleep: 'Sleep',
  Diaper: 'Diaper Change',
  Weight: 'Weight'
}

// Simulated AI insights
const INSIGHTS = [
  "Great job starting your tracking streak! Keep logging feeding and sleep to unlock personalized insights about your baby's rhythm. 🌟",
  "Your baby's feeding pattern looks consistent today! Consistency helps establish healthy routines. 💪",
  "Sleep and feeding close together? That's normal! Babies often sleep better after a good feed. 😴",
  "You're on a roll! Keep tracking to unlock more detailed insights about sleep and feeding patterns. ⭐",
  "Tracking diaper changes helps monitor hydration - great attention to detail! 🎯"
]

export default class extends Controller {
  static targets = ["logList", "insightContainer", "insightButton", "emptyState"]

  connect() {
    this.logs = []
    this.updateUI()
  }

  addLog(event) {
    const button = event.currentTarget
    const type = button.dataset.type
    const category = button.dataset.category

    const newLog = {
      id: Math.random().toString(36).substr(2, 9),
      type: type,
      category: category,
      timestamp: new Date()
    }

    this.logs.unshift(newLog)
    if (this.logs.length > 5) {
      this.logs.pop()
    }

    this.clearInsight()
    this.updateUI()
  }

  generateInsight() {
    if (this.logs.length === 0) return

    const button = this.insightButtonTarget
    button.disabled = true
    button.innerHTML = '<span class="animate-pulse">Analyzing...</span>'

    // Simulate API delay
    setTimeout(() => {
      const randomInsight = INSIGHTS[Math.floor(Math.random() * INSIGHTS.length)]
      this.showInsight(randomInsight)
      this.updateInsightButton()
    }, 1000)
  }

  showInsight(text) {
    this.insightContainerTarget.innerHTML = `
      <div class="bg-white border-2 border-[#FFC800] p-4 rounded-2xl insight-card shadow-sm relative overflow-hidden">
        <div class="absolute top-0 left-0 w-full h-1 bg-[#FFC800]"></div>
        <div class="flex items-start gap-3 relative z-10">
          <div class="bg-[#FFC800] text-white p-2 rounded-lg shrink-0 shadow-sm">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/>
              <path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/>
            </svg>
          </div>
          <div>
            <h5 class="font-extrabold text-[#D7A800] text-xs uppercase mb-1 tracking-widest">BabyLog AI Says:</h5>
            <p class="text-sm text-gray-700 font-bold leading-relaxed">${text}</p>
          </div>
        </div>
      </div>
    `
  }

  clearInsight() {
    this.insightContainerTarget.innerHTML = ''
    this.updateInsightButton()
  }

  updateUI() {
    this.updateLogList()
    this.updateInsightButton()
  }

  updateLogList() {
    if (this.logs.length === 0) {
      this.logListTarget.innerHTML = `
        <div class="text-center py-10 text-gray-300 italic border-2 border-dashed border-gray-100 rounded-2xl bg-gray-50">
          Tap buttons to start tracking!
        </div>
      `
      return
    }

    const getCategoryColor = (category) => {
      switch (category) {
        case EventCategory.Feeding: return 'bg-[#FFC800]'
        case EventCategory.Health: return 'bg-[#CE82FF]'
        case EventCategory.Growth: return 'bg-[#FF4B4B]'
        default: return 'bg-[#58CC02]'
      }
    }

    this.logListTarget.innerHTML = this.logs.map(log => `
      <div class="flex items-center justify-between p-3 bg-white border-2 border-gray-100 rounded-xl log-entry shadow-sm">
        <div class="flex items-center gap-3">
          <div class="w-2 h-8 rounded-full ${getCategoryColor(log.category)}"></div>
          <div class="flex flex-col">
            <span class="font-extrabold text-gray-700 text-sm">${log.type}</span>
            <span class="text-[10px] font-bold text-gray-400 uppercase tracking-wide">${log.category}</span>
          </div>
        </div>
        <span class="text-xs font-bold text-gray-400">Just now</span>
      </div>
    `).join('')
  }

  updateInsightButton() {
    const hasInsight = this.insightContainerTarget.innerHTML.trim() !== ''
    const hasLogs = this.logs.length > 0

    if (hasInsight) {
      this.insightButtonTarget.classList.add('hidden')
    } else {
      this.insightButtonTarget.classList.remove('hidden')
      this.insightButtonTarget.disabled = !hasLogs
      this.insightButtonTarget.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block mr-2">
          <path d="M12 4.5a2.5 2.5 0 0 0-4.96-.46 2.5 2.5 0 0 0-1.98 3 2.5 2.5 0 0 0-1.32 4.24 3 3 0 0 0 .34 5.58 2.5 2.5 0 0 0 2.96 3.08A2.5 2.5 0 0 0 9.5 22h5a2.5 2.5 0 0 0 2.46-2.06 2.5 2.5 0 0 0 2.96-3.08 3 3 0 0 0 .34-5.58 2.5 2.5 0 0 0-1.32-4.24 2.5 2.5 0 0 0-1.98-3A2.5 2.5 0 0 0 12 4.5"/>
          <path d="m15.7 10.4-.9.4"/><path d="m9.2 13.2-.9.4"/><path d="m13.6 15.7-.4-.9"/><path d="m10.8 9.2-.4-.9"/><path d="m15.7 13.5-.9-.4"/><path d="m9.2 10.9-.9-.4"/><path d="m10.5 15.7.4-.9"/><path d="m13.1 9.2.4-.9"/>
        </svg>
        Get AI Insight
      `
    }
  }
}
